import { useEffect, useMemo, useState } from "react";
import { Checkbox, Modal, Select, Tag, message } from "antd";
import { useRecoilState, useRecoilValue } from "recoil";
import { Button as MhmButton } from "../../misc/Button";
import { brandKitsState, locationsState } from "../../../../state/atoms";
import BrandKitApi from "../../../../api/BrandKitApi";
import { BrandKitDto } from "../../../../generated/dashboard-client";

const ALL_VALUE = -1;

interface Props {
  open: boolean;
  brandKit: BrandKitDto | null;
  onClose: () => void;
  onAssigned?: (updated: BrandKitDto) => void;
}

export function AssignBrandToLocationModal({ open, brandKit, onClose, onAssigned }: Props) {
  const locations = useRecoilValue(locationsState);
  const [brandKits, setBrandKits] = useRecoilState(brandKitsState);

  const [assignLocationIds, setAssignLocationIds] = useState<number[]>([]);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (open && brandKit) {
      setAssignLocationIds(brandKit.locationIds || []);
    }
  }, [open, brandKit]);

  // A location may only have one brand. Map each location to the *other* brand that owns it (if any).
  const locationToOtherBrand = useMemo(() => {
    const map = new Map<number, BrandKitDto>();
    brandKits.forEach((b) => {
      if (b.id === brandKit?.id) return;
      (b.locationIds || []).forEach((locId) => map.set(locId, b));
    });
    return map;
  }, [brandKits, brandKit?.id]);

  const selectableLocationIds = locations
    .map((l) => l.internalId!)
    .filter((id) => !locationToOtherBrand.has(id));
  const allSelected =
    selectableLocationIds.length > 0 &&
    selectableLocationIds.every((id) => assignLocationIds.includes(id));
  const allLocationsSelected =
    locations.length > 0 && locations.every((l) => assignLocationIds.includes(l.internalId!));

  const handleChange = (vals: number[]) => {
    if (vals.includes(ALL_VALUE)) {
      setAssignLocationIds(allSelected ? [] : selectableLocationIds);
      return;
    }
    // Defensive: drop any value that belongs to another brand.
    setAssignLocationIds(vals.filter((id) => !locationToOtherBrand.has(id)));
  };

  const tagRender = (props: any) => {
    if (allLocationsSelected) {
      if (props.value !== locations[0]?.internalId) return <span style={{ display: "none" }} />;
      return (
        <Tag
          closable
          onClose={(e: any) => {
            e.preventDefault();
            setAssignLocationIds([]);
          }}
          style={{ marginRight: 3 }}
        >
          All locations
        </Tag>
      );
    }
    return (
      <Tag
        closable
        onClose={(e: any) => {
          e.preventDefault();
          props.onClose();
        }}
        style={{ marginRight: 3 }}
      >
        {props.label}
      </Tag>
    );
  };

  const handleAssign = () => {
    if (!brandKit?.id) return;
    setAssigning(true);
    BrandKitApi.update(brandKit.id, {
      ...brandKit,
      locationIds: assignLocationIds,
    })
      .then((resp) => {
        setBrandKits(brandKits.map((b) => (b.id === resp.data.id ? resp.data : b)));
        message.success("Brand assigned");
        onAssigned?.(resp.data);
        onClose();
      })
      .catch(() => message.error("Failed to assign brand"))
      .finally(() => setAssigning(false));
  };

  return (
    <Modal
      className={"assign-brand-modal clean one"}
      open={open}
      onCancel={onClose}
      footer={null}
    >
      <header>
        <h2>Assign Brand to Location</h2>
      </header>
      <p className={"assign-brand-subtitle"}>
        {brandKit?.name
          ? <>Choose locations to assign <b>{brandKit.name}</b></>
          : "Choose locations to assign brand"}
      </p>

      <div className={"assign-brand-body"}>
        <Select
          mode="multiple"
          className="assign-brand-select"
          size="large"
          style={{ width: "100%" }}
          placeholder="Select"
          value={assignLocationIds}
          onChange={(vals) => handleChange(vals as number[])}
          optionFilterProp="label"
          optionLabelProp="label"
          maxTagCount="responsive"
          tagRender={tagRender}
          menuItemSelectedIcon={null}
          dropdownClassName="assign-brand-dropdown"
          autoClearSearchValue={false}
        >
          {locationToOtherBrand.size === 0 &&
            <Select.Option value={ALL_VALUE} label="All locations" className="assign-brand-option">
            <Checkbox checked={allSelected} />
            <span>All locations</span>
          </Select.Option>}
          {locations.map((loc) => {
            const otherBrand = locationToOtherBrand.get(loc.internalId!);
            const disabled = !!otherBrand;
            return (
              <Select.Option
                key={loc.internalId}
                value={loc.internalId!}
                label={loc.name}
                className="assign-brand-option"
                disabled={disabled}
              >
                <Checkbox checked={assignLocationIds.includes(loc.internalId!)} disabled={disabled} />
                <span>{loc.name}</span>
                {otherBrand && (
                  <span style={{ marginLeft: 8, color: "#999", fontSize: 12 }}>
                    (assigned to "{otherBrand.name}")
                  </span>
                )}
              </Select.Option>
            );
          })}
        </Select>
      </div>

      <footer>
        <MhmButton type={"Cancel"} onClick={onClose}>
          <span>Cancel</span>
        </MhmButton>
        <MhmButton type={"Primary"} onClick={handleAssign} loading={assigning}>
          <span>Assign</span>
        </MhmButton>
      </footer>
    </Modal>
  );
}
